/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * App — public Brandbox Studio orchestrator.
 *
 * Phase K+2 refactor (2026-05-29): the 3,448-line god component became
 * this < 200-line route shell. Business logic lives in
 * `hooks/useConfigurator`; UI is delegated to `components/*` and
 * `components/modals/*`. Content + flows arrive via `ContentProvider`
 * (lib/content.tsx) — JSON fallbacks for first render, API merge for
 * live edits. Pixel baseline preserved (apps/public/tests/visual/).
 */

import { useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { PreviewBanner } from './components/PreviewBanner';
import { LandingPage } from './components/LandingPage';
import { ShoppingCart } from './components/ShoppingCart';
import { CheckoutPage } from './components/CheckoutPage';
import { ConfiguratorView } from './components/ConfiguratorView';
import { SuccessView } from './components/SuccessView';
import { ClearConfirmModal } from './components/modals/ClearConfirmModal';
import { AbandonModal } from './components/modals/AbandonModal';
import { MaxMerchModal } from './components/modals/MaxMerchModal';
import { MaxFinalTouchModal } from './components/modals/MaxFinalTouchModal';
import { ConfigErrorModal } from './components/modals/ConfigErrorModal';
import { QuantityPromptModal } from './components/modals/QuantityPromptModal';
import {
  ContentProvider,
  useFlows,
  useIsPreview,
  useLandingContent,
  useLoadFlow,
} from './lib/content';
import { getPreviewFlowId } from './lib/preview';
import { getOptionSteps } from './lib/orderPipeline';
import { useConfigurator } from './hooks/useConfigurator';

function AppShell() {
  const flows = useFlows();
  const loadFlow = useLoadFlow();
  const { categories } = useLandingContent('categories');
  const defaults = useLandingContent('defaults');
  const emailTemplate = useLandingContent('email-template');
  const isPreview = useIsPreview();
  // N2b — the draft flow id from `?preview=<flowId>` (stable for the session).
  const previewFlowId = useMemo(() => getPreviewFlowId(), []);

  const cfg = useConfigurator({
    flows,
    categories,
    defaults,
    emailTemplate,
    loadFlow,
    isPreview,
    previewFlowId,
  });

  // N6b — the ConfigErrorModal "fix it" deep-links must target the CURRENT
  // box's own first two option steps, not the literal `packaging`/`merch`
  // ids. For Spring/Office these resolve to exactly `packaging` + `merch`
  // (byte-identical), but a novel admin box (e.g. vessel → treats → wrap)
  // now jumps to its REAL step ids instead of dead-linking to a step that
  // doesn't exist. Derived from the flow's own option-step list (the same
  // positional convention the cart/checkout gate uses).
  const optionSteps = useMemo(() => getOptionSteps(cfg.flow), [cfg.flow]);
  const firstOptionStepId = optionSteps[0]?.id;
  const secondOptionStepId = optionSteps[1]?.id;
  // N6c — the modal "Missing X / Go to X" copy reads the box's own step titles
  // (positional slot 0/1). For Spring/Office these are "Packaging" / "Merch",
  // so the copy is unchanged; a novel box (vessel/treats) shows its real titles.
  const firstOptionStepTitle = optionSteps[0]?.title;
  const secondOptionStepTitle = optionSteps[1]?.title;

  return (
    <div className="min-h-screen bg-[#fcfcfc] font-century selection:bg-[#57c858]/30">
      <PreviewBanner />
      {cfg.isSubmitted ? (
        <SuccessView
          cart={cfg.cart}
          customerInfo={cfg.customerInfo}
          reference={cfg.submissionRef}
          onReturnHome={cfg.resetAfterSuccess}
        />
      ) : cfg.view === 'landing' ? (
        <LandingPage onSelectCategory={cfg.handleCategorySelection} flow={cfg.flow} />
      ) : cfg.view === 'checkout' ? (
        <CheckoutPage
          cart={cfg.cart}
          flow={cfg.flow}
          selections={cfg.selections}
          defaults={defaults}
          onUpdateQuantity={cfg.updateQuantity}
          onRemoveItem={cfg.removeItem}
          onCheckout={cfg.handleSubmit}
          onBack={() => {
            cfg.setView('configurator');
            cfg.setCurrentStepIndex(1);
            window.scrollTo(0, 0);
          }}
          onNavigateToStep={cfg.goToStep}
          onSelectCategory={cfg.handleCategorySelection}
          customerInfo={cfg.customerInfo}
          setCustomerInfo={cfg.setCustomerInfo}
          deliveryDate={cfg.deliveryDate}
          setDeliveryDate={cfg.setDeliveryDate}
          notes={cfg.notes}
          setNotes={cfg.setNotes}
          logos={cfg.logos}
          setLogos={cfg.setLogos}
          isSubmitting={cfg.isSubmitting}
          logoUploadFailed={cfg.logoUploadFailed}
          error={cfg.error}
          storage={cfg.storage}
          setStorage={cfg.setStorage}
          shipment={cfg.shipment}
          setShipment={cfg.setShipment}
          setShowConfigErrorModal={(open) => cfg.setModal(open ? 'configError' : null)}
        />
      ) : (
        <>
          <ConfiguratorView
            flow={cfg.flow}
            cart={cfg.cart}
            defaults={defaults}
            currentStep={cfg.currentStep}
            currentStepIndex={cfg.currentStepIndex}
            selections={cfg.selections}
            customerInfo={cfg.customerInfo}
            setCustomerInfo={cfg.setCustomerInfo}
            selectedCategory={cfg.selectedCategory}
            selectedBox={cfg.selectedBox}
            logos={cfg.logos}
            isSubmitting={cfg.isSubmitting}
            error={cfg.error}
            otherValues={cfg.otherValues}
            setStepOther={cfg.setStepOther}
            qtyInputRef={cfg.qtyInputRef}
            prevStep={cfg.prevStep}
            nextStep={cfg.nextStep}
            goToStep={cfg.goToStep}
            toggleOption={cfg.toggleOption}
            updateQuantity={cfg.updateQuantity}
            removeItem={cfg.removeItem}
            handleLogoUpload={cfg.handleLogoUpload}
            handlePricingImport={cfg.handlePricingImport}
            handleSubmit={cfg.handleSubmit}
            checkDependencies={cfg.checkDependencies}
            isStepValid={cfg.isStepValid}
            setIsCartOpen={cfg.setIsCartOpen}
          />
          <AnimatePresence>
            {cfg.isCartOpen && (
              <ShoppingCart
                cart={cfg.cart}
                flow={cfg.flow}
                onClose={() => cfg.setIsCartOpen(false)}
                onNavigateToOffer={() => {
                  cfg.setView('checkout');
                  cfg.setIsCartOpen(false);
                  window.scrollTo(0, 0);
                }}
                onAddMore={() => {
                  cfg.setIsCartOpen(false);
                  window.scrollTo(0, 0);
                }}
                onClear={() => {
                  cfg.setModal('clearConfirm');
                  cfg.setIsCartOpen(false);
                }}
                setShowConfigErrorModal={(open) =>
                  cfg.setModal(open ? 'configError' : null)
                }
              />
            )}
          </AnimatePresence>
        </>
      )}

      {/* AnimatePresence requires a unique `key` on EVERY direct child to
          track its presence. These six modal children previously had none,
          so motion saw six children with the same empty key `` and React
          warned on every re-render. Keying each by its modal kind fixes it. */}
      <AnimatePresence>
        <ClearConfirmModal
          key="modal-clearConfirm"
          open={cfg.modal === 'clearConfirm'}
          onClose={() => cfg.setModal(null)}
          onConfirm={cfg.clearCart}
        />
        <AbandonModal
          key="modal-abandon"
          open={cfg.modal === 'abandon'}
          onClose={() => cfg.setModal(null)}
          abandonEmail={cfg.abandonEmail}
          setAbandonEmail={cfg.setAbandonEmail}
        />
        <MaxMerchModal
          key="modal-maxMerch"
          open={cfg.modal === 'maxMerch'}
          max={cfg.capModalMax}
          onClose={() => cfg.setModal(null)}
        />
        <MaxFinalTouchModal
          key="modal-maxFinalTouch"
          open={cfg.modal === 'maxFinalTouch'}
          max={cfg.capModalMax}
          onClose={() => cfg.setModal(null)}
        />
        <ConfigErrorModal
          key="modal-configError"
          open={cfg.modal === 'configError'}
          onClose={() => cfg.setModal(null)}
          missingPackaging={!cfg.cart.box.packagingId}
          missingMerch={cfg.cart.box.merchIds.length === 0}
          packagingTitle={firstOptionStepTitle}
          merchTitle={secondOptionStepTitle}
          onGoToPackaging={() =>
            firstOptionStepId && cfg.goToConfiguratorStep(firstOptionStepId)
          }
          onGoToMerch={() =>
            secondOptionStepId && cfg.goToConfiguratorStep(secondOptionStepId)
          }
        />
        <QuantityPromptModal
          key="modal-quantityPrompt"
          open={cfg.modal === 'quantityPrompt'}
          qty={cfg.cart.qty}
          onUpdateQuantity={cfg.updateQuantity}
          onConfirm={() => {
            cfg.setHasSetQuantity(true);
            cfg.setModal(null);
          }}
        />
      </AnimatePresence>
    </div>
  );
}

export default function App() {
  return (
    <ContentProvider>
      <AppShell />
    </ContentProvider>
  );
}
